import { GetResolverOptionsForCMS } from "aldoo-ra/Store/Availability"
import LocalizationController from "aldoo-ra/Localization/localization-controller"

export default {
    preFormat: (fieldValues, Type) => {
        if (!Type || Type.type !== "StoreProduct") return fieldValues;
        if (!fieldValues) return fieldValues;

        const fieldTypes = Type.fields;
        for (const field of fieldTypes) {
            if (!field) continue;
            if (!fieldValues[field.id]) continue;

            // NOTE: Don't convert database model references to IDs in preFormat
            // Let the database-model formatter handle fetching full objects
            // Only do ID conversions in postFormat when saving

            // Handle availability field - convert array of strings back to option object (single selection)
            if (field.id === "availability") {
                const value = fieldValues[field.id];
                if (Array.isArray(value) && value.length > 0) {
                    // Take the first resolver from the array (for single selection)
                    const firstResolver = value[0];
                    
                    let resolverValue = firstResolver;
                    // Extract the value string if it's an object
                    if (typeof firstResolver === "object" && firstResolver.value) {
                        resolverValue = firstResolver.value;
                    }
                    
                    if (typeof resolverValue === "string") {
                        // Find the matching option from the resolver options
                        const options = GetResolverOptionsForCMS();
                        const matchingOption = options.find(opt => opt.value === resolverValue);
                        if (matchingOption) {
                            fieldValues[field.id] = matchingOption;
                        }
                    }
                }
            }

            // Handle supported_platforms field - ensure it's an array of strings
            if (field.id === "supported_platforms") {
                const value = fieldValues[field.id];
                if (Array.isArray(value)) {
                    // Convert objects to strings if needed
                    fieldValues[field.id] = value.map(item => {
                        if (typeof item === "object" && item.value) {
                            return item.value;
                        }
                        return typeof item === "string" ? item : String(item);
                    });
                } else if (value && typeof value !== "string") {
                    // Handle single non-string value
                    if (typeof value === "object" && value.value) {
                        fieldValues[field.id] = [value.value];
                    } else {
                        fieldValues[field.id] = [String(value)];
                    }
                } else if (typeof value === "string") {
                    fieldValues[field.id] = [value];
                } else {
                    // Default fallback
                    fieldValues[field.id] = ["any"];
                }
            }

            // Handle bundle items - but keep database references intact for editing
            if (field.id === "bundle" && Array.isArray(fieldValues.bundle)) {
                fieldValues.bundle = fieldValues.bundle.map(item => {
                    const bundleItem = { ...item };
                    // Don't convert database references to IDs in preFormat
                    // Let them remain as full objects for editing
                    return bundleItem;
                });
            }
        }

        return fieldValues;
    },

    postFormat: async (fieldValues, Type) => {
        if (!Type || Type.type !== "StoreProduct") return fieldValues;
        if (!fieldValues) return fieldValues;

        // Handle localized fields - sync with primary language value
        // This ensures the database has the English value for localized fields
        const localizedFields = ['name', 'description'];
        if (fieldValues._localization) {
            const { GetLocalizationKeys } = await import('aldoo-ra/Localization/api');

            for (const fieldId of localizedFields) {
                const field = Type.fields.find(f => f.id === fieldId);
                if (!field?.localization) continue;

                // Find the localization entry for this field
                const localization = fieldValues._localization.find(loc => loc.field === fieldId);
                if (!localization?.localizationKey) continue;

                try {
                    // Get the English (primary) translation
                    const primaryValue = await GetLocalizationKeys("en", [localization.localizationKey]);
                    const englishValue = primaryValue?.[localization.localizationKey];

                    // Update the field with the primary language value
                    if (englishValue && englishValue !== `{{${localization.localizationKey}}}`) {
                        fieldValues[fieldId] = englishValue;
                    }
                } catch (error) {
                    console.warn(`Failed to sync localized ${fieldId} field:`, error);
                }
            }
        }

        const fieldTypes = Type.fields;
        for (const field of fieldTypes) {
            if (!field) continue;
            if (!fieldValues[field.id]) continue;

            // Handle price field
            if (field.id === "price") {
                if (fieldValues[field.id].model === "StorePrice") {
                    fieldValues[field.id] = fieldValues[field.id].item;
                } else if (fieldValues[field.id]._id) {
                    fieldValues[field.id] = fieldValues[field.id]._id;
                }
            }

            // Handle licenseModel field
            if (field.id === "licenseModel") {
                if (fieldValues[field.id].model === "ProductLicenseModel") {
                    fieldValues[field.id] = fieldValues[field.id].item;
                } else if (fieldValues[field.id]._id) {
                    fieldValues[field.id] = fieldValues[field.id]._id;
                }
            }

            // Handle type field - convert option object to string
            if (field.id === "type") {
                const value = fieldValues[field.id];
                if (typeof value === "object" && value?.value) {
                    fieldValues[field.id] = value.value;
                } else if (typeof value !== "string") {
                    fieldValues[field.id] = String(value);
                }
            }

            // Handle availability field - convert option object to string value
            if (field.id === "availability") {
                const value = fieldValues[field.id];
                if (typeof value === "object" && value?.value) {
                    fieldValues[field.id] = value.value;
                } else if (Array.isArray(value) && value.length > 0) {
                    // If it's an array, take first item and extract value
                    const firstItem = value[0];
                    if (typeof firstItem === "object" && firstItem?.value) {
                        fieldValues[field.id] = firstItem.value;
                    } else if (typeof firstItem === "string") {
                        fieldValues[field.id] = firstItem;
                    }
                } else if (typeof value !== "string" && value != null) {
                    fieldValues[field.id] = String(value);
                }
            }

            // Handle supported_platforms field - ensure it's an array of strings for saving
            if (field.id === "supported_platforms") {
                const value = fieldValues[field.id];
                if (Array.isArray(value)) {
                    // Ensure all items are strings
                    fieldValues[field.id] = value.map(item => {
                        if (typeof item === "object" && item.value) {
                            return item.value;
                        }
                        return typeof item === "string" ? item : String(item);
                    });
                } else if (value) {
                    // Convert single value to array
                    if (typeof value === "object" && value.value) {
                        fieldValues[field.id] = [value.value];
                    } else {
                        fieldValues[field.id] = [String(value)];
                    }
                } else {
                    // Default fallback
                    fieldValues[field.id] = ["any"];
                }
            }

            // Handle bundle items
            if (field.id === "bundle" && Array.isArray(fieldValues.bundle)) {
                fieldValues.bundle = fieldValues.bundle.map(item => {
                    const bundleItem = { ...item };

                    // Convert product reference to ID
                    if (bundleItem.product?.model === "StoreProduct") {
                        bundleItem.product = bundleItem.product.item;
                    } else if (bundleItem.product?._id) {
                        bundleItem.product = bundleItem.product._id;
                    }

                    // Convert price reference to ID if it exists
                    if (bundleItem.price?.model === "StorePrice") {
                        bundleItem.price = bundleItem.price.item;
                    } else if (bundleItem.price?._id) {
                        bundleItem.price = bundleItem.price._id;
                    }

                    return bundleItem;
                });
            }
        }

        return fieldValues;
    }
}